import React, { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiUsers,
  FiUserPlus,
  FiUserCheck,
  FiUserX,
  FiShield,
  FiClock,
  FiAlertCircle, 
  FiHome,         
  FiUser,        
  // FiActivity,  
} from "react-icons/fi";
import "../assets/css/UserDetails.css";
import { useNavigate } from "react-router-dom";
import { searchUser } from "../Service/apiService";
let cachedStats = {
  total: 0,
  new: 0,
  renewed: 0,
  lost: 0,
  active: 0,
  unpaid: 0,
  trial: 0,
  internal: 0,
  upcoming: 0,
};
const hasValidStats = (stats) => {
  return Object.values(stats).some(value => value > 0);
};

export default function UsersDetails() {
  const navigate = useNavigate();
     const [loading, setLoading] = useState(() => !hasValidStats(cachedStats));
   
  const [stats, setStats] = useState(() => ({ ...cachedStats }));

  useEffect(() => {
     // Only fetch if we don't have valid cached data
     if (!hasValidStats(cachedStats)) {
       fetchStats();
     }
   }, []);

  const handleUsersData = (key) => {
    navigate(`/dashboard/users/${key}`);
  };

  const parsePossibleDate = (value) => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return !isNaN(parsed) ? new Date(parsed) : null;
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      // Fetch all users (Pharma mode)
      const response = await searchUser("?userDeleted=false", { industry: "" });
      const raw = response?.result || [];

      const toLower = (val) => (val || "").toLowerCase().trim();

      // Helper: check if date is within next N days
      const isInFutureRange = (dateStr, days = 90) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const now = new Date();
        const futureLimit = new Date();
        futureLimit.setDate(now.getDate() + days);
        return date > now && date <= futureLimit;
      };

      // 1. Total Users (exclude internal)
      const totalUsers = raw.filter(
      (u) => toLower(u.companyType) !== "internal"
    );

      // 2. New Users
      const newUsers = raw.filter(
        (u) =>
          toLower(u.companyType) === "new" &&
          u.invoiceDate &&
          u.activeStatus === true
      );

      // 3. Renewed Users
  
    const renewedUsers = raw.filter(
      (u) =>
        u.invoiceDate &&                    // critical: must have invoiceDate
        u.activeStatus === true &&
        toLower(u.companyType) === "renewal" &&
        toLower(u.paymentStatus) === "paid"
        // No date filter (default = all-time)
    );

      // 4. Lost Users
      const lostUsers = raw.filter((u) => toLower(u.companyType) === "lost");

      // 5. Active Users
      const activeUsers = raw.filter(
        (u) =>
          u.invoiceDate &&
          u.activeStatus === true &&
          toLower(u.companyType) !== "lost" &&
          toLower(u.companyType) !== "internal"
      );

      // 6. Unpaid Users
      const unpaidUsers = raw.filter(
        (u) =>
          u.activeStatus === true &&
          toLower(u.paymentStatus) === "unpaid" &&
          toLower(u.companyType) !== "internal"
      );

      // 7. Trial Users
      const trialUsers = raw.filter(
        (u) =>
          toLower(u.companyType) === "trial" &&
          toLower(u.paymentStatus) === "trial" &&
          u.activeStatus === true
      );

      // 8. Internal Users
      const internalUsers = raw.filter(
        (u) => toLower(u.companyType) === "internal"
      );

      // 9. Upcoming Renewals (next 90 days)
      const upcomingUsers = raw.filter(
        (u) =>
          toLower(u.companyType) === "renewal" &&
          u.activeStatus === true &&
          isInFutureRange(u.subEndDate, 90)
      );

      const computed = {
        total: totalUsers.length,
        new: newUsers.length,
        renewed: renewedUsers.length,
        lost: lostUsers.length,
        active: activeUsers.length,
        unpaid: unpaidUsers.length,
        trial: trialUsers.length,
        internal: internalUsers.length,
        upcoming: upcomingUsers.length,
      };
      setStats(computed);
      cachedStats = { ...computed };
    } catch (error) {
      console.warn("Failed to load user stats:", error);
    }finally {
      setLoading(false); 
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "—";
    return Number(num).toLocaleString();
  };

const metrics = [
  {
    id: "total-users",
    key: "totalUsers",
    label: "Total Users",
    value: formatNumber(stats.total),
    icon: FiUsers,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "new-users",
    key: "newUsers",
    label: "New Users",
    value: formatNumber(stats.new),
    icon: FiUserPlus,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "renewed-users",
    key: "renewedUsers",
    label: "Renewed Users",
    value: formatNumber(stats.renewed),
    icon: FiUserCheck,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "lost-users",
    key: "lostUsers",
    label: "Lost Users",
    value: formatNumber(stats.lost),
    icon: FiUserX,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "active-users",
    key: "activeUsers",
    label: "Active Users",
    value: formatNumber(stats.active),
    icon: FiUser, 
    color: "rgb(5, 55, 99)",
  },
  {
    id: "unpaid-users",
    key: "unpaidUsers",
    label: "Unpaid Users",
    value: formatNumber(stats.unpaid),
    icon: FiAlertCircle,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "trial-users",
    key: "trialUsers",
    label: "Trial Users",
    value: formatNumber(stats.trial),
    icon: FiClock,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "internal-users",
    key: "internalUsers",
    label: "Internal Users",
    value: formatNumber(stats.internal),
    icon: FiHome,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "upcoming-users",
    key: "upcomingUsers",
    label: "Upcoming Renewals",
    value: formatNumber(stats.upcoming),
    icon: FiClock,
    color: "rgb(5, 55, 99)",
  },
];

  // Skeleton loader for numbers only
const NumberSkeleton = () => (
  <div className="number-skeleton">
    <div className="skeleton-loader"></div>
  </div>
);

  return (
    <div className="user-details-container">
      <div className="metrics-grid">
        {metrics.map(({ id, label, icon: Icon, color, value, key }) => (
          <div
            key={id}
            className="metric-card"
            onClick={() => handleUsersData(key)}
          >
            <div className="metric-icon" style={{ backgroundColor: color }}>
              <Icon size={18} />
            </div>
             <p className="metric-label">{label}</p>
      
   
      {loading ? (
        <NumberSkeleton />
      ) : (
        <p className="metric-value">{value}</p>
      )}
          </div>
        ))}
      </div>
    </div>
  );
}
